import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import { ICollection, ICollectionData } from '@/types/collection';
import { IProduct } from '@/types/general';
import {
  QueryKey,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import Cookies from 'js-cookie';

const COLLECTION_QUERY_KEY = 'collection-list' as const;
const COLLECTION_PREVIEW_QUERY_KEY = 'collection-preview-list' as const;
const COLLECTION_OPTIONS_QUERY_KEY = 'collection-options' as const;

export const collectionsQueryKeys = queryKeysFactory(COLLECTION_QUERY_KEY);
export const collectionsPreviewQueryKeys = queryKeysFactory(COLLECTION_PREVIEW_QUERY_KEY);
export const collectionsOptionsQueryKeys = queryKeysFactory(COLLECTION_OPTIONS_QUERY_KEY);

const isPartner = Cookies.get('_partner_id') !== undefined;

export const useCollections = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StoreCollectionListResponse,
      FetchError,
      HttpTypes.StoreCollectionListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: collectionsQueryKeys.list(query),
    queryFn: async () =>
      isPartner ? sdk.partner.collections.list(query) : sdk.admin.collections.list(query),
    ...options,
  });

  return { ...data, ...rest };
};

export const useDetailCollection = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<ICollectionData>,
      FetchError,
      HttpTypes.IResponse<ICollectionData>
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: collectionsQueryKeys.detail(id),
    queryFn: async () =>
      isPartner ? sdk.partner.collections.detail(id) : sdk.admin.collections.detail(id),
    ...options,
  });

  return { ...data, ...rest };
};

export const useInfiniteCollections = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.StoreCollectionsListInfiniteResponse,
      FetchError,
      HttpTypes.StoreCollectionsListInfiniteResponse,
      HttpTypes.StoreCollectionsListInfiniteResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  return useInfiniteQuery({
    queryKey: collectionsPreviewQueryKeys.list(query),
    queryFn: async ({ pageParam = 1 }) => {
      const result = await (isPartner
        ? sdk.partner.collections.list({ ...query, page: pageParam as number })
        : sdk.admin.collections.list({ ...query, page: pageParam as number }));

      return {
        data: result?.data ?? [],
        totalCount: result?.totalCount ?? 0,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = lastPage?.data?.length ?? 0;
      const currentPage = allPages?.length ?? 0;

      if (currentPage * pageSize < Number(lastPage?.totalCount ?? 0)) {
        return currentPage + 1;
      }

      return undefined;
    },
    ...options,
  });
};

export const useCreateCollection = (
  options?: UseMutationOptions<HttpTypes.IResponse<ICollection>, FetchError, ICollection>
) => {
  return useMutation({
    mutationFn: (data) =>
      isPartner ? sdk.partner.collections.create(data) : sdk.admin.collections.create(data),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useUpdateCollection = (
  options?: UseMutationOptions<HttpTypes.IResponse<ICollection>, FetchError, ICollection>
) => {
  return useMutation({
    mutationFn: (data) =>
      isPartner ? sdk.partner.collections.update(data) : sdk.admin.collections.update(data),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDeleteCollection = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminCollectionDeleteResponse>,
    FetchError,
    string[]
  >
) => {
  return useMutation({
    mutationFn: (ids) =>
      isPartner
        ? (sdk.partner.collections.delete({ ids }) as unknown as Promise<
            HttpTypes.IResponse<HttpTypes.AdminCollectionDeleteResponse>
          >)
        : (sdk.admin.collections.delete({ ids }) as unknown as Promise<
            HttpTypes.IResponse<HttpTypes.AdminCollectionDeleteResponse>
          >),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDuplicateCollections = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<{ id: string; title: string }> | string,
    FetchError,
    string
  >
) => {
  return useMutation({
    mutationFn: (query: string) => sdk.admin.collections.duplicate(query),
    onSuccess(response, variables, context) {
      if (typeof response !== 'string' && response?.data) {
        options?.onSuccess?.(response, variables, context);
      } else {
        options?.onSuccess?.(response, variables, context);
      }
    },
    onError(error, variables, context) {
      options?.onError?.(error as FetchError, variables, context);
    },
    ...options,
  });
};

export const useCatalogOmni = (
  query: HttpTypes.FindParams & {
    categoryIds?: string[];
  },
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<IProduct[]>,
      FetchError,
      HttpTypes.IResponse<IProduct[]>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: collectionsQueryKeys.list(query),
    queryFn: async () => sdk.site.general.listCatalogOmni(query),
    ...options,
  });

  return { data, ...rest };
};

export const useGetCollectionsOptions = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.StoreCollectionListResponse,
      FetchError,
      HttpTypes.StoreCollectionListResponse,
      HttpTypes.StoreCollectionListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  // Generate a unique key that includes all query parameters
  // and an additional resetKey if provided in options
  const queryKey = collectionsOptionsQueryKeys.list({
    ...query,
    // Extract the resetKey from options if it exists
    resetKey: options?.meta?.resetKey,
  });

  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }) =>
      sdk.admin.collections.listOptions({ ...query, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const { data, totalCount } = lastPage;

      // If no data or data is empty, there's no next page
      if (!data || data.length === 0) {
        return undefined;
      }

      // If totalCount is not provided or invalid, use data length as fallback
      const total = totalCount ? Number(totalCount) : 0;

      // Calculate current items count and page size
      const currentPage = allPages.length;
      const pageSize = query.pageSize || data.length;
      const totalFetched = currentPage * pageSize;

      // Only fetch next page if we haven't fetched all items yet
      // Add extra condition: make sure we've received some data this page
      if (total > 0 && totalFetched < total && data.length > 0) {
        return currentPage + 1;
      }

      return undefined;
    },
    ...options,
  });
};
